import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  disabled?: boolean;
  name?: string;
  onRemove?: () => void;
  selected?: string;
  value?: string;
}

function useInputValue ({
  value,
  children,
  badgeRef,
}: {
  value?: string;
  children?: React.ReactNode;
  badgeRef: React.RefObject<HTMLDivElement>;
}) {
  const [derivedValue, setDerivedValue] = useState<string | undefined>(
    undefined
  )

  const inputValue = useMemo(() => {
    if (value !== undefined) return value
    if (typeof children === 'string') return children

    return undefined
  }, [value, children])

  useEffect(() => {
    if (inputValue === undefined) {
      setDerivedValue(badgeRef.current?.textContent || '')
    }
  }, [inputValue])

  return inputValue ?? derivedValue
}

/**
 * A variation of the `<Badge>` component purpose-built for use within the
 * `<BadgeInput />` component:
 *
 * - Its height is tweaked to fit within the content box of an empty `<BadgeInput />`.
 * - It renders a hidden input for the submitting selected values with forms
 * - It interacts with the `<BadgeInput />` to allow values to be selected
 *   and removed by keyboard and screen reader users.
 */
export function Badge ({ children, disabled, name, onRemove, selected, value, ...props }: BadgeProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const badgeRef = useRef<HTMLDivElement>(null)

  const inputValue = useInputValue({ value, children, badgeRef })

  useEffect(() => {
    const badgeElement = badgeRef.current

    if (badgeElement) {
      function handleRemove () {
        onRemove?.()
      }

      badgeElement.addEventListener('badge-remove', handleRemove)

      return () => {
        badgeElement?.removeEventListener('badge-remove', handleRemove)
      }
    }
  }, [onRemove])

  return (
    <div
      {...props}
      aria-roledescription='value'
      className={clsx('rounded-md border h-[1.375rem] body-sm flex items-center leading-none pl-2', {
        'border-thatch-300 bg-thatch-100 text-thatch-600': selected,
        'border-gray-200 bg-gray-800/[0.025] text-gray-800': !selected,
      })}
      data-badge=''
      data-selected={selected ? '' : undefined}
      ref={badgeRef}
      role='group'
    >
      {children}
      {onRemove && (
        <button
          aria-label='Remove'
          className={clsx(
            'appearance-none flex size-[1.375rem] rounded-md items-center justify-center transition outline outline-2 outline-offset-0 outline-transparent focus-visible:outline-thatch-500',
            {
              'text-thatch-400': selected,
              'text-gray-500 hover:text-gray-800 disabled:text-gray-500': !selected,
            }
          )}
          disabled={disabled}
          onClick={onRemove}
          // Keyboard users will use roving focus to select and remove badges
          tabIndex={-1}
        >
          <XIcon size={12} />
        </button>
      )}
      <input name={name} ref={hiddenInputRef} type='hidden' value={inputValue ?? ''} />
    </div>
  )
}

Badge.displayName = 'BadgeInput.Badge'
